import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "../components/DashboardLayout";
import { API } from "../lib/constants";

const CATEGORIES = ["All", "Blog", "Social Media", "Advertising", "Email", "Product"];

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/content/templates`);
      setTemplates(data);
    } catch {
      // Failed
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filtered = templates.filter((t) => {
    const matchesCategory = activeCategory === "All" || t.category === activeCategory;
    const matchesSearch = !searchQuery || t.name.toLowerCase().includes(searchQuery.toLowerCase()) || t.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const openTemplate = (template) => {
    navigate(`/dashboard/text?template=${template.id}`);
  };

  const categoryCount = (cat) => {
    if (cat === "All") return templates.length;
    return templates.filter((t) => t.category === cat).length;
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Templates Library</h1>
          <p className="text-gray-600">Start with a proven template and customize it for your needs</p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="w-full max-w-md border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg"
            data-testid="template-search"
          />
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 font-medium text-sm transition-colors rounded-lg ${
                activeCategory === cat
                  ? "bg-[#0066FF] text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-[#0066FF] hover:text-[#0066FF]"
              }`}
              data-testid={`category-${cat.toLowerCase().replace(' ', '-')}`}
            >
              {cat} ({categoryCount(cat)})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading templates...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-black p-12 text-center" data-testid="no-templates">
            <p className="text-gray-500">No templates found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((template) => (
              <div
                key={template.id}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-[#0066FF] hover:shadow-lg transition-all group"
                data-testid={`template-card-${template.id}`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-3 py-1 bg-[#0066FF]/10 text-[#0066FF] text-xs font-bold rounded-full">
                      {template.category}
                    </span>
                    <span className="text-xs text-gray-400">{template.variables.length} fields</span>
                  </div>
                  <h3 className="text-lg font-bold mb-2 group-hover:text-[#0066FF] transition-colors">{template.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{template.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {template.variables.map((v) => (
                      <span key={v.name} className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
                        {v.label}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => openTemplate(template)}
                    className="w-full bg-[#0066FF] text-white py-2.5 font-bold hover:bg-[#0052CC] transition-colors rounded-lg text-sm"
                    data-testid={`use-template-${template.id}`}
                  >
                    Use Template
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
